/**
 * Shared role & team constants used across UserManagement and IssueForm.
 * Single source of truth — update here to reflect everywhere.
 */

// All assignable roles (excludes superadmin which is system-only)
export const ALL_ROLES = [
  { value: 'developer',  label: 'Developer',        team: 'Frontend' },
  { value: 'tester',     label: 'QA / Tester',       team: 'QA' },
  { value: 'designer',   label: 'Design / Designer', team: 'Design' },
  { value: 'devops',     label: 'DevOps',             team: 'DevOps' },
  { value: 'analyst',    label: 'Business Analyst',  team: 'Business' },
  { value: 'product',    label: 'Product',            team: 'Product' },
  { value: 'product_manager', label: 'Product Manager', team: 'Product' },
  { value: 'lead',       label: 'Tech Lead',          team: 'Mobile App' },
  { value: 'manager',    label: 'Manager',            team: 'Management' },
  { value: 'hod',        label: 'Head of Department', team: 'Management' },
  { value: 'hr',         label: 'HR Generalist',      team: 'HR' },
  { value: 'marketing_manager', label: 'Marketing Manager', team: 'Marketing' },
  { value: 'brand_manager',     label: 'Brand Manager',     team: 'Brand' },
  { value: 'pr_manager',        label: 'PR Manager',        team: 'PR' },
  { value: 'content_marketer',  label: 'Content Marketing Specialist', team: 'Content' },
  { value: 'marketing_designer', label: 'Designer', team: 'Design' },
  { value: 'sales_marketing', label: 'Sales & Marketing', team: 'Marketing' },
  { value: 'trainee',         label: 'Trainee',           team: 'Marketing' },
  { value: 'it_support',     label: 'IT Support Specialist', team: 'IT' },
  { value: 'hr_recruiter',   label: 'Recruiter / Talent Acquisition', team: 'HR' },
  { value: 'member',     label: 'Member',             team: 'Mobile App' },
  { value: 'admin',      label: 'Admin',              team: 'Management' },
];

/** Map RBAC role → preferred default team when department context is unknown. */
export const ROLE_DEFAULT_TEAM = Object.fromEntries(
  ALL_ROLES.map((r) => [r.value, r.team])
);

// Quick lookup: role value -> label
export const ROLE_LABEL = Object.fromEntries(ALL_ROLES.map(r => [r.value, r.label]));

// Role metadata for UI badges/colors (includes superadmin for display)
export const ROLE_META = {
  superadmin: { color: '#7c3aed', bg: '#ede9fe', label: 'Super Admin' },
  admin:      { color: '#2C63AE', bg: '#EAF6FD', label: 'Admin' },
  manager:    { color: '#d97706', bg: '#fef3c7', label: 'Manager' },
  hod:        { color: '#c2410c', bg: '#ffedd5', label: 'Head of Department' },
  lead:       { color: '#0369a1', bg: '#e0f2fe', label: 'Tech Lead' },
  product:    { color: '#7c3aed', bg: '#ede9fe', label: 'Product' },
  product_manager: { color: '#6d28d9', bg: '#ede9fe', label: 'Product Manager' },
  analyst:    { color: '#4f46e5', bg: '#eef2ff', label: 'Business Analyst' },
  developer:  { color: '#0891b2', bg: '#cffafe', label: 'Developer' },
  tester:     { color: '#059669', bg: '#d1fae5', label: 'QA / Tester' },
  designer:   { color: '#db2777', bg: '#fce7f3', label: 'Design / Designer' },
  devops:     { color: '#ea580c', bg: '#ffedd5', label: 'DevOps' },
  member:     { color: '#475569', bg: '#f1f5f9', label: 'Member' },
  hr:         { color: '#be185d', bg: '#fce7f3', label: 'HR Generalist' },
  hr_recruiter: { color: '#9d174d', bg: '#fce7f3', label: 'Recruiter' },
  sales_marketing: { color: '#0d9488', bg: '#ccfbf1', label: 'Sales & Marketing' },
  marketing_manager: { color: '#0f766e', bg: '#ccfbf1', label: 'Marketing Manager' },
  brand_manager: { color: '#115e59', bg: '#ccfbf1', label: 'Brand Manager' },
  pr_manager: { color: '#0e7490', bg: '#cffafe', label: 'PR Manager' },
  content_marketer: { color: '#14b8a6', bg: '#ccfbf1', label: 'Content Marketing' },
  marketing_designer: { color: '#db2777', bg: '#fce7f3', label: 'Designer' },
  trainee: { color: '#64748b', bg: '#f1f5f9', label: 'Trainee' },
  it_support: { color: '#64748b', bg: '#f1f5f9', label: 'IT Support' },
};

// Team options for the "Assign to Team" dropdown in IssueForm.
// Each entry has a value (stored on the issue), a display label, and an
// optional role so the person filter can match by role when team name differs.
export const TEAMS = [
  { value: 'Any',         label: 'Any' },
  { value: 'Frontend',    label: 'Frontend',          role: 'developer' },
  { value: 'Backend',     label: 'Backend',           role: 'developer' },
  { value: 'QA',          label: 'QA / Tester',       role: 'tester' },
  { value: 'DevOps',      label: 'DevOps',            role: 'devops' },
  { value: 'Design',      label: 'Design / Designer', role: 'designer' },
  { value: 'Product',     label: 'Product',           role: 'product' },
  { value: 'Mobile App',  label: 'Mobile App',        role: 'developer' },
  { value: 'Business',    label: 'Business Analyst',  role: 'analyst' },
  { value: 'Management',  label: 'Management',        role: 'manager' },
  { value: 'Marketing',   label: 'Marketing',         role: 'sales_marketing' },
  { value: 'Sales',       label: 'Sales',             role: 'sales_marketing' },
  { value: 'Content',     label: 'Content',           role: 'content_marketer' },
  { value: 'Brand',       label: 'Brand',             role: 'brand_manager' },
  { value: 'Design',      label: 'Design',            role: 'marketing_designer' },
  { value: 'PR',          label: 'PR',                role: 'pr_manager' },
  { value: 'Full-stack',  label: 'Full-stack',        role: 'developer' },
  { value: 'IT',          label: 'IT',                role: 'it_support' },
  { value: 'HR',          label: 'HR',                role: 'hr' },
];
